// W A S D キーでキャンバスを pan する。
// 押下中フラグを保持し、requestAnimationFrame ループで pan 量を加算することで滑らかに動く。
// SHIFT を併せて押すと 2 倍速で移動する。
// IME や input 要素にフォーカスがあるときは無効化する。

import { useEffect } from "react";
import { useCanvasStore } from "../store/canvas-store";

const PAN_SPEED_PX_PER_FRAME = 12;
const SHIFT_SPEED_MULTIPLIER = 2;

export function useKeyboardPan(): void {
  const setView = useCanvasStore((s) => s.setView);

  useEffect(() => {
    const pressed = new Set<string>();
    let shiftDown = false;
    let rafId: number | null = null;

    const tick = (): void => {
      const { view } = useCanvasStore.getState();
      const speed = PAN_SPEED_PX_PER_FRAME * (shiftDown ? SHIFT_SPEED_MULTIPLIER : 1);
      let dx = 0;
      let dy = 0;
      if (pressed.has("w")) dy += speed;
      if (pressed.has("s")) dy -= speed;
      if (pressed.has("a")) dx += speed;
      if (pressed.has("d")) dx -= speed;
      if (dx !== 0 || dy !== 0) {
        setView({ ...view, panX: view.panX + dx, panY: view.panY + dy });
      }
      rafId = window.requestAnimationFrame(tick);
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.shiftKey) shiftDown = true;
      // Shift 単独はパン対象キーではない。Ctrl/Meta/Alt と組み合わせは pan を無効化する。
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isEditableTarget(event.target)) return;
      const key = event.key.toLowerCase();
      if (["w", "a", "s", "d"].includes(key)) {
        pressed.add(key);
        if (rafId === null) {
          rafId = window.requestAnimationFrame(tick);
        }
        event.preventDefault();
      }
    };

    const onKeyUp = (event: KeyboardEvent): void => {
      if (!event.shiftKey) shiftDown = false;
      const key = event.key.toLowerCase();
      pressed.delete(key);
      if (pressed.size === 0 && rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    // ウィンドウのフォーカスが外れた直後に Shift キーが上がっても拾えるよう、blur で状態をリセット。
    const onBlur = (): void => {
      shiftDown = false;
      pressed.clear();
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [setView]);
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select";
}
